/**
 * Cliente HTTP do exame de assinatura eletrônica (E74.F1, ADR-0075) — raia isolada.
 *
 * Espelha apps/api/types/exame_eletronico.py e apps/api/runtime/exame_eletronico.py.
 * O escritório transcreve o protocolo da plataforma de assinatura por CCB, salva
 * (PUT) e manda verificar (POST): o servidor recomputa o código de verificação
 * (SHA-256) da peça, confronta com o declarado, confere identidade e IDs, monta a
 * linha do tempo e devolve o quadro. Nada aqui conhece a triagem visual nem o LR.
 */
import type { FonteDoEsperado, ObjetoBloqueadoData } from '@/lib/exame_eletronico_custodia';
import { classifyExameEletronicoError } from '@/lib/exame_eletronico_erros';
import type { AxiosInstance } from 'axios';
import type {
  AssinaturaDigitalApuradaData,
  VereditoAssinaturaDigital,
} from '@/lib/exame_eletronico_assinatura_digital';
import type {
  ClassificacaoIpData,
  CoordenadaAvaliadaData,
  EnderecoClusterData,
} from '@/lib/exame_eletronico_enriquecimento';
import type { FonteDoIdentificador } from '@/lib/abertura_do_caso';
import type { LeituraPendenteData } from '@/lib/exame_eletronico_emissao';
import type { CodigosLidosApuradoData } from '@/lib/exame_eletronico_codigos_lidos';
import type { EncadeamentoApuradoData } from '@/lib/exame_eletronico_encadeamento';
import type { ExameEletronicoCcbData } from '@/lib/exame_eletronico_ccb';
import type { JornadaApuradaData } from '@/lib/exame_eletronico_jornada';
import type { LocalizacaoApuradaData, MapaDoExameData } from '@/lib/exame_eletronico_localizacao';
import type { MetadadosApuradosData } from '@/lib/exame_eletronico_metadados';
import type { Tendencia } from '@/lib/pre_analise';

export type { Modalidade } from '@/lib/cases';
export type { ExameEletronicoCcbData } from '@/lib/exame_eletronico_ccb';
export type {
  AssinaturaDigitalApuradaData,
  AssinaturaEmbutidaData,
  CertificadoSignatarioData,
  ChaveAssinaturaDigital,
  VereditoAssinaturaDigital,
} from '@/lib/exame_eletronico_assinatura_digital';

export type {
  ClassificacaoIpData,
  CoordenadaAvaliadaData,
  EnderecoClusterData,
  FaixaDistancia,
  QualidadeCoordenada,
  TipoIp,
  VinculoCluster,
} from '@/lib/exame_eletronico_enriquecimento';
export type {
  ChaveEncadeamento,
  EncadeamentoApuradoData,
} from '@/lib/exame_eletronico_encadeamento';
export { CHAVES_ENCADEAMENTO } from '@/lib/exame_eletronico_encadeamento';
export type {
  ChaveCodigosLidos,
  CodigoConfrontadoData,
  CodigoLidoData,
  CodigosLidosApuradoData,
} from '@/lib/exame_eletronico_codigos_lidos';
export { CHAVES_CODIGOS_LIDOS } from '@/lib/exame_eletronico_codigos_lidos';
export type {
  ChaveMetadado,
  FonteDasDatas,
  MetadadosApuradosData,
  MetadadosDaPecaData,
} from '@/lib/exame_eletronico_metadados';
export { CHAVES_METADADOS } from '@/lib/exame_eletronico_metadados';
export type {
  ChaveJornada,
  FonteDoInstante,
  InstanteDaJornadaData,
  JornadaApuradaData,
  RotuloDoInstante,
} from '@/lib/exame_eletronico_jornada';
export { CHAVES_JORNADA, FONTES_DO_INSTANTE } from '@/lib/exame_eletronico_jornada';

export type {
  BiometriaProtocoloData,
  CodigoDeclaradoInput,
  CoordenadaProtocoloData,
  DispositivoProtocoloData,
  EnvelopeDoProtocoloData,
  EventoProtocoloData,
  FonteCoordenada,
  FormatoDaData,
  IpProtocoloData,
  PapelIp,
  ProtocoloAssinaturaCasoData,
  ProtocoloAssinaturaData,
  SignatarioProtocoloData,
  TipoAssinatura,
  TipoDaPeca,
  TipoEventoProtocolo,
} from '@/lib/exame_eletronico_protocolo';
export type { LidoDosDocumentosData } from '@/lib/exame_eletronico_lido_dos_documentos';
export {
  FONTES_COORDENADA,
  FORMATOS_DA_DATA,
  PAPEIS_IP,
  TIPOS_ASSINATURA,
  TIPOS_EVENTO,
  protocoloDoCaso,
} from '@/lib/exame_eletronico_protocolo';

import type {
  CodigoDeclaradoInput,
  FormatoDaData,
  ProtocoloAssinaturaCasoData,
  ProtocoloAssinaturaData,
  TipoDaPeca,
  TipoEventoProtocolo,
} from '@/lib/exame_eletronico_protocolo';

// Contrato — saída (quadro do exame) ---------------------------------------

/** E162.F1.T07 — `inconclusivo`: o dado veio e o material não o confirma nem contradiz. */
export type SituacaoItem = 'confere' | 'nao_confere' | 'ausente' | 'inconclusivo' | 'nao_executado';
export type SeveridadeFlag = 'alta' | 'media';
export type GrauConvergencia = 'forte' | 'medio' | 'fraco' | 'inconclusivo' | 'inconsistente';
export type ExameEletronicoStatus = 'rascunho' | 'verificado' | 'concluido';

/** Chaves estáveis dos itens do quadro — a UI rotula por chave. */
export type ChaveIntegridade = 'hash_ccb' | 'custodia_interna';
export type ChaveIdentidade =
  | 'cpf_signatario'
  | 'nome_signatario'
  | 'envelope_id'
  | 'assinatura_id'
  | 'sessao_biometrica'
  | 'tipo_assinatura';
/** Estágios que dependem de provedor externo (E74.F2). */
export type ChaveEnriquecimento = 'cluster_cadastral' | 'geocodificacao' | 'classificacao_ip';
export interface ItemVerificacaoData {
  chave: string;
  situacao: SituacaoItem;
  detalhe?: string | null;
  esperado?: string | null;
  obtido?: string | null;
  fonte_do_esperado?: FonteDoEsperado | null;
}

export interface RedFlagData {
  codigo: string;
  severidade: SeveridadeFlag;
  detalhe?: string | null;
}

export interface EventoLinhaDoTempoData {
  tipo: TipoEventoProtocolo;
  em_utc?: string | null;
  em_local?: string | null;
  fora_de_ordem: boolean;
  utc_local_incoerentes: boolean;
  /** E106.F2 — o carimbo do instrumento entra com fonte "instrumento" e o documento. */
  fonte?: 'protocolo' | 'instrumento';
  descricao?: string | null;
}

export interface ResultadoExameEletronicoData {
  integridade: ItemVerificacaoData[];
  identidade: ItemVerificacaoData[];
  linha_do_tempo: EventoLinhaDoTempoData[];
  sequencia_ilogica: boolean;
  /** Situação de cada estágio com provedor externo (E74.F2). */
  enriquecimento: ItemVerificacaoData[];
  ips: ClassificacaoIpData[];
  cluster: EnderecoClusterData[];
  coordenada: CoordenadaAvaliadaData | null;
  coordenada_situacao: SituacaoItem;
  /**
   * E74.F4 — `null` é o exame verificado ANTES desta Fase existir. Distingue
   * "não havia esse estágio" de "o estágio rodou e não achou assinatura".
   */
  assinatura_digital?: AssinaturaDigitalApuradaData | null;
  /**
   * E76.F4 (ADR-0076) — veredito subordinado do estágio, calculado no backend.
   * `null`/ausente é o exame anterior ao veredito: a UI não renderiza alegação.
   */
  assinatura_digital_veredito?: VereditoAssinaturaDigital | null;
  /**
   * E84.F2 — o que o próprio arquivo declara sobre si (datas, produtor,
   * gravações), confrontado com o instante do ato. `null`/ausente é o exame
   * verificado antes da Fase: a UI mostra a nota de legado, não um eixo vazio.
   */
  metadados?: MetadadosApuradosData | null;
  /** E84.F3 — o vínculo lido do texto da peça; nulo no exame anterior à Fase. */
  encadeamento?: EncadeamentoApuradoData | null;
  /** E157.F2 — os códigos lidos das páginas e o confronto; nulo no exame anterior à régua v48. */
  codigos_lidos?: CodigosLidosApuradoData | null;
  /** E84.F4 — a linha do tempo consolidada por fonte; nula no exame anterior à Fase. */
  jornada?: JornadaApuradaData | null;
  /** E90.F2 — município do ato, endereço declarado e endereço de rede; nulo antes da Fase. */
  localizacao?: LocalizacaoApuradaData | null;
  /** E90.F3 — os dois mapas (ADR-0081); nulo no exame verificado antes da Fase. */
  mapas?: MapaDoExameData[] | null;
  red_flags: RedFlagData[];
  ausencias: string[];
  grau_convergencia: GrauConvergencia;
  parametros_versao: string;
  executado_em: string;
}

/** Uma reabertura da conclusão: quem tinha assinado, com que grau e por quê caiu. */
export interface ReaberturaRegistroData {
  reaberto_em: string;
  reaberto_por: string | null;
  motivo: string;
  grau_anterior: GrauConvergencia | null;
  concluido_em: string | null;
  concluido_por: string | null;
}

export interface ExameEletronicoRecordData {
  id: string;
  case_id: string;
  source_document_id: string;
  dados_protocolo: ProtocoloAssinaturaData;
  resultado: ResultadoExameEletronicoData | null;
  grau_convergencia: GrauConvergencia | null;
  tendencia: Tendencia | null;
  status: ExameEletronicoStatus;
  parametros_versao: string | null;
  grau_justificativa?: string | null;
  conclusao_texto?: string | null;
  observacoes_perito?: string | null;
  verificado_em: string | null;
  concluido_em: string | null;
  concluido_por?: string | null;
  historico_reaberturas?: ReaberturaRegistroData[];
  created_by?: string | null;
  created_at: string;
  updated_at: string;
}

/** Item da lista `GET /exame-eletronico`: uma entrada por CCB do caso. */
/** Resposta do `GET /exame-eletronico` desde a E78.F2: protocolo + peças. */
export interface ExameEletronicoCasoData {
  protocolo: ProtocoloAssinaturaCasoData | null;
  pecas: ExameEletronicoCcbData[];
  /** E84.F5 (GUS-44 RF05) — a régua vigente, para a tabela avisar resultado antigo. */
  parametros_versao_corrente?: string;
  /**
   * E88.F2 (GUS-30 RF06) — o formato que o CATÁLOGO propõe para a plataforma
   * declarada. Sustenta a procedência da dica do formato da data (GUS-44 CA02)
   * depois que a importação pela tela saiu.
   */
  formato_da_data_do_catalogo?: FormatoDaData | null;
  /**
   * E86.F1 (GUS-43 RF03) — documentos obrigatórios do kit da instituição que
   * nenhuma peça declarou ser. Vazio = kit completo, instituição fora do
   * catálogo, ou protocolo sem plataforma. Sinaliza, nunca bloqueia.
   */
  kit_pendente?: TipoDaPeca[];
  /**
   * E91.F1 (GUS-51 RF02) — a etapa fotográfica não tem status de servidor: a
   * conclusão dela é o parecer facial gravado. Ausente no payload vale `false`
   * (o servidor sempre envia; os dublês dos specs alheios não precisam saber).
   */
  conferencia_facial_realizada?: boolean;
  /** E118.F1 (GUS-127): leituras propostas sem confirmação nem corroboração — avisa na emissão. */
  leituras_pendentes?: LeituraPendenteData[];
  banco_do_caso?: string | null; // E119.F2 (GUS-132, Z-04): `null` = caso sem banco; a tela avisa.
  pecas_paradas_pela_custodia?: ObjetoBloqueadoData[]; // E136.F1 (GUS-152): fora do exame.
  /** E177.F6 (GUS-253): a CCB do caso aberto por CCB, com as contagens; nulo fora dele. */
  ccb_do_caso?: CcbDaMesaData | null;
  /** E177.F6: a divergência que quem enviou manteve na abertura (`ccb_divergente_mantida`). */
  divergencia_mantida?: DivergenciaMantidaData | null;
}

/** E177.F6 (GUS-253, ADR-0130): a CCB do caso na mesa. */
export interface CcbDaMesaData {
  numero: string;
  documentos: number;
  compartilhados: number;
}

/** E177.F6: o número mantido diante do identificador lido no pacote, da trilha do caso. */
export interface DivergenciaMantidaData {
  numero: string;
  lida: string;
  fonte: FonteDoIdentificador;
}

export type { FonteDoEsperado, ObjetoBloqueadoData } from '@/lib/exame_eletronico_custodia';

export type { LeituraPendenteData } from '@/lib/exame_eletronico_emissao';

// Erros tipados: E136.F3 os levou a `exame_eletronico_erros` (o teto de linhas deste módulo).
export {
  classifyExameEletronicoError,
  ExameEletronicoError,
  type ExameEletronicoErrorReason,
} from '@/lib/exame_eletronico_erros';

// Rotas -------------------------------------------------------------------

function basePath(caseId: string): string {
  return `/casos/${caseId}/exame-eletronico`;
}

export async function listExamesEletronicos(
  client: AxiosInstance,
  caseId: string,
): Promise<ExameEletronicoCasoData> {
  try {
    const { data } = await client.get<ExameEletronicoCasoData>(basePath(caseId));
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/**
 * Grava/substitui o protocolo do CASO (E78.F2).
 *
 * Recusado com `protocolo_peca_concluida` quando alguma peça já tem conclusão
 * do perito: o protocolo alimenta todas elas (ADR-0077 §2.4).
 */
export async function salvarProtocoloDoCaso(
  client: AxiosInstance,
  caseId: string,
  protocolo: ProtocoloAssinaturaCasoData,
): Promise<ProtocoloAssinaturaCasoData> {
  try {
    const { data } = await client.put<ProtocoloAssinaturaCasoData>(
      `${basePath(caseId)}/protocolo`,
      protocolo,
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/** Grava o código declarado e as observações da peça; invalida a verificação dela. */
export async function salvarCodigoDaPeca(
  client: AxiosInstance,
  caseId: string,
  sourceDocumentId: string,
  payload: CodigoDeclaradoInput,
): Promise<ExameEletronicoRecordData> {
  try {
    const { data } = await client.put<ExameEletronicoRecordData>(
      `${basePath(caseId)}/${sourceDocumentId}/codigo`,
      payload,
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/**
 * Roda o motor sobre as N peças vivas do caso; peça concluída é pulada.
 *
 * `atualizarConsultas` vale para o CASO: a consulta paga é refeita uma vez, não
 * uma por peça (ADR-0077 §2.5).
 */
export async function verificarCaso(
  client: AxiosInstance,
  caseId: string,
  options: { atualizarConsultas?: boolean } = {},
): Promise<ExameEletronicoRecordData[]> {
  try {
    const { data } = await client.post<ExameEletronicoRecordData[]>(
      `${basePath(caseId)}/verificar`,
      { atualizar_consultas: options.atualizarConsultas ?? false },
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/**
 * Roda a verificação determinística e devolve o exame com o quadro.
 *
 * `atualizarConsultas` refaz as consultas às fontes externas — é a única ação
 * que gasta consulta paga. Sem ela, o servidor reusa o que já consultou neste
 * caso, e duas verificações do mesmo protocolo devolvem o mesmo quadro.
 */
export async function verificarExame(
  client: AxiosInstance,
  caseId: string,
  sourceDocumentId: string,
  options: { atualizarConsultas?: boolean } = {},
): Promise<ExameEletronicoRecordData> {
  try {
    const { data } = await client.post<ExameEletronicoRecordData>(
      `${basePath(caseId)}/${sourceDocumentId}/verificar`,
      { atualizar_consultas: options.atualizarConsultas ?? false },
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/** Corpo do `POST /concluir` — a decisão do perito sobre a CCB. */
export interface ConclusaoEletronicaInput {
  grau: GrauConvergencia;
  grau_justificativa?: string | null;
  conclusao_texto: string;
  observacoes_perito?: string | null;
}

/**
 * Fixa a decisão do perito e fecha o exame da CCB.
 *
 * Reclassificar o grau apurado exige justificativa: o servidor recusa com
 * `justificativa_requerida`, e a régua é dele — a tela desabilita o botão antes
 * por conveniência, não como garantia.
 */
export async function concluirExame(
  client: AxiosInstance,
  caseId: string,
  sourceDocumentId: string,
  conclusao: ConclusaoEletronicaInput,
): Promise<ExameEletronicoRecordData> {
  try {
    const { data } = await client.post<ExameEletronicoRecordData>(
      `${basePath(caseId)}/${sourceDocumentId}/concluir`,
      conclusao,
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/**
 * Desfaz a conclusão da CCB, guardando no histórico o que caiu.
 *
 * Recusado depois que a certificação daquela CCB foi requerida
 * (`certificacao_requerida`): ali o cliente já recebeu o resultado.
 */
export async function reabrirExame(
  client: AxiosInstance,
  caseId: string,
  sourceDocumentId: string,
  motivo: string,
): Promise<ExameEletronicoRecordData> {
  try {
    const { data } = await client.post<ExameEletronicoRecordData>(
      `${basePath(caseId)}/${sourceDocumentId}/reabrir`,
      { motivo },
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}
